import React, {
  Fragment,
  useEffect,
  useMemo,
  useState,
  createRef,
  useRef,
} from "react";
import Prism from "prismjs";
import * as PR from "prismjs/themes/prism.css";
import { useStyletron } from "baseui";
import { connect } from "react-redux";
import { Button, KIND, SHAPE, SIZE } from "baseui/button";
import { styled } from "styletron-react";
import { useLocation, useRouteMatch } from "react-router";
import { RESOURCE_TYPES } from "../../constants/AppEnums";
import { FaPencilAlt } from "react-icons/fa";
import Blank from "baseui/icon/blank";
import { FaSave } from "react-icons/fa";
import Show from "baseui/icon/show";
import {
  fetchFileContent,
  updateFileContent,
} from "../../redux/template/TemplateThunk";
import { Spinner, SIZE as SSIZE } from "baseui/spinner";
import { ProgressBar, SIZE as PSIZE } from "baseui/progress-bar";
import { Block } from "baseui/block";
import PipelineFlowWithProvider, { PARSE_STRATEGIES } from "../../components/code-view-editor/PipelineFlowComponent";
import CodeViewEditor from "../../components/code-view-editor/CodeViewEditorComponent";
import useRouteParams from "../../hooks/useRouteParams";

const ButtonContainer = styled("div", {
  display: "flex",
  justifyContent: "space-between",
  padding: "10px 10px 0px 10px",
  margin: "10px",
});

const PreStyled = styled("pre", {
  whiteSpace: "pre-wrap", // Allows the text to wrap
  wordWrap: "break-word",
  fontSize: "10px",
});

function TemplateTextFileContentView(props) {
  const match = useRouteMatch();
  const { pathParams, searchParams, searchPart } = useRouteParams();
  const [css] = useStyletron();
  const [originalContent, setOriginalContent] = useState(
    props.fileContent || ""
  );
  const [content, setContent] = useState(props.fileContent || "");
  const [viewMode, setViewMode] = useState(0);
  const [isSaving, setIsSaving] = useState(false);

  const [childKey, setitemschildKey] = useState(7);
  const forceUpdate = () => {
    setitemschildKey(childKey * 89);
  };

  useEffect(() => {
    if (searchParams["fileId"]) {
      props.fetchFileContent(match.params?.templateId, searchParams["fileId"]);
    }
  }, []);

  useEffect(() => {
    setOriginalContent(props.fileContent || "");
    setContent(props.fileContent || "");
  }, [props.fileContent]);

  const isContentChanged = originalContent !== content;

  useEffect(() => {
    forceUpdate();
  }, [props.fileContent, viewMode]);

  function saveFileContent() {
    if (searchParams["fileId"] && isContentChanged) {
      props.updateFileContent(
        match.params?.templateId,
        searchParams["fileId"],
        content
      );
    }
  }

  const handleEditorChange = (value) => {
    // Normalize line endings when updating content
    setContent(value.replace(/\r\n/g, "\n"));
  };

  return (
    <Fragment>
      {searchParams["fileId"] ? (
        <Block>
          <ButtonContainer>
            <Block>
              <Button
                shape={SHAPE.pill}
                size={SIZE.mini}
                kind={viewMode ? KIND.secondary : KIND.primary}
                startEnhancer={() => <Show size={24} />}
                endEnhancer={() => <Blank size={5} />}
                onClick={(e) => setViewMode(0)}
                $style={{ marginRight: "10px" }}
              >
                View
              </Button>
              <Button
                shape={SHAPE.pill}
                size={SIZE.mini}
                kind={viewMode ? KIND.primary : KIND.secondary}
                startEnhancer={() => <FaPencilAlt size={24} />}
                endEnhancer={() => <Blank size={5} />}
                onClick={(e) => setViewMode(1)}
              >
                Edit
              </Button>
            </Block>
            <Button
              shape={SHAPE.pill}
              size={SIZE.mini}
              disabled={!isContentChanged}
              startEnhancer={() =>
                isSaving ? <Spinner size={SSIZE.small} /> : <FaSave size={24} />
              }
              endEnhancer={() => <Blank size={5} />}
              onClick={(e) => saveFileContent()}
            >
              Save
            </Button>
          </ButtonContainer>
          <Block
            $style={{
              opacity: props.fileContentLoading ? 1 : 0,
              pointerEvents: props.fileContentLoading ? "auto" : "none",
            }}
          >
            <ProgressBar size={PSIZE.large} infinite />
          </Block>
          
          {viewMode === 0 && ["postprocess.yaml", "postprocess.yml"].includes(
            props.selectedFolder?.name?.toLowerCase()
          ) ? (
            <PipelineFlowWithProvider
              key={content}
              yamlText={content}
              parseStrategy={PARSE_STRATEGIES.PIPELINE_STEPS}
              containerStyle={{ height: '800px' }}
              enableAutoFit={true}
              showNodeAccordion={true}
            />
          ) : (
            <CodeViewEditor
              fileName={props.selectedFolder?.name}
              viewMode={viewMode}
              content={content}
              onContentChange={handleEditorChange}
              parameterGroups={props.activeTemplate?.parameterGroups}
            />
          )}
        </Block>
      ) : (
        <></>
      )}
    </Fragment>
  );
}

const mapStateToProps = (state) => {
  return {
    activeTemplate: state.template.activeTemplate,
    selectedFolder: state.template.activeTemplate?.selectedFolder,
    templateAccess: state.template.activeTemplate?.access,
    fileContentLoading: state.template.activeTemplate?.fileContentLoading,
    fileContent: state.template.activeTemplate?.fileContent,
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    fetchFileContent: (templateId, fileId) =>
      dispatch(fetchFileContent(templateId, fileId)),
    updateFileContent: (templateId, fileId, content) =>
      dispatch(updateFileContent(templateId, fileId, content)),
    // Add your dispatch actions here
  };
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(TemplateTextFileContentView);
